'use client';
import { Banner } from '@airbus/components-react';
import Spinners from '../../shared/components/Spinner';
import Layout from '../../shared/components/layout';
import useFetchScreenDetails from '../../shared/utils/useFetchScreenDetails';
import GetAllColumnContextProvider, {
  useColumnContextAPI,
} from '../../shared/context/GetAllColumnContext';
import { useEffect, useState } from 'react';
import HeaderScreenList from '../../features/attributes_access/components/HeaderScreenList';
import { useConfigContext } from '../../shared/context/ConfigContext';
import { t } from '../../i18next';
import { getChildTableDetailsService } from '../../features/getChildTableDetailsService';
import { CommitHandler } from '../../features/attributes_access/CommitHandler';
import SetColumnConfig from '../../shared/components/grid/SetColumnConfig';
import { useScreensDropdownOptions } from '../../features/admin';
import GridTable from '../../shared/components/grid/GridTable';

const AttributesAccessScreen = () => {
  const { loading, error, screenTables } = useFetchScreenDetails('attributes_access');

  return (
    <>
      {loading ? (
        <Spinners />
      ) : (
        <Layout>
          {error && <Banner variant='warning'>{t('data_not_available')}</Banner>}
          {!screenTables
            ? null
            : screenTables.map((item) => (
                <GetAllColumnContextProvider key={item.id}>
                  <AttributesAccessWrapper tableRes={item} />
                </GetAllColumnContextProvider>
              ))}
        </Layout>
      )}
    </>
  );
};

export default AttributesAccessScreen;

function AttributesAccessWrapper({ tableRes }) {
  const screenGridId = tableRes.id;
  const [gridOptions, setGridOptions] = useState([]);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [selectedGrid, setSelectedGrid] = useState(null);
  const { configSchema } = useConfigContext();
  const { screenOptions } = useScreensDropdownOptions(screenGridId);

  useEffect(() => {
    setSelectedScreen(screenOptions[0]?.value);
  }, [screenOptions]);

  useEffect(() => {
    if (selectedScreen) {
      getChildTableDetailsService(screenGridId, selectedScreen, configSchema).then((res) => {
        const options = res[`${configSchema}_mbd_screen_to_data_grids`]?.map((item) => ({
          value: item.id,
          label: item.mbdDatagrid.dataGridInternalName,
        }));
        setGridOptions(options);
        setSelectedGrid(options[0].value);
      });
    }
  }, [selectedScreen]);

  return (
    <>
      <AttributesAccess
        tableRes={tableRes}
        screenOptions={screenOptions}
        selectedScreen={selectedScreen}
        setSelectedScreen={setSelectedScreen}
        gridOptions={gridOptions}
        selectedGrid={selectedGrid}
        setSelectedGrid={setSelectedGrid}
      />
    </>
  );
}

const AttributesAccess = ({
  tableRes,
  screenOptions,
  setSelectedScreen,
  selectedScreen,
  gridOptions,
  selectedGrid,
  setSelectedGrid,
}) => {
  const { updateCustomConfig } = useColumnContextAPI();

  useEffect(() => {
    updateCustomConfig({
      ScreenSlotProps: {
        screen: 'attributeAccess',
        updateValue: setSelectedScreen,
        initialValue: selectedScreen,
        updateGridValue: setSelectedGrid,
        initialGridValue: selectedGrid,
        screenOptions,
        gridOptions,
      },
      AdditionalHeaderComp: [{ name: 'headerScreenList', Component: HeaderScreenList }],
      SetIsCellEditable,
      SetColumnConfig: AttributeColumnConfig,
      CommitHandler,
    });
  }, [selectedScreen, screenOptions, setSelectedGrid, gridOptions]);

  return <GridTable tableRes={tableRes} />;
};

function SetIsCellEditable(props) {
  const { field, row, access } = props;
  let isEditable = true;
  if ((field === 'role_id' || field === 'screen_grid_attr_config_id') && row.status !== 'N') {
    isEditable = false;
    return isEditable;
  }
  const rolePermission = access?.[field]?.accessLevel === 'U';
  return row.status !== 'D' && access?.edit_row && isEditable && rolePermission;
}

function AttributeColumnConfig(props) {
  function accessLevelOnChange(props) {
    const { row, value, setAlertbar } = props;
    if (value === 'R' && row.status === 'N') {
      setAlertbar({
        children: t('access_level-read'),
        severity: 'warning',
        stopAutoClose: true,
      });
      return false;
    }
  }
  const { gridColumns: columnsConfig } = SetColumnConfig(props);
  const gridColumns = columnsConfig.reduce((resp, item) => {
    if (item.attrInternalName === 'access_level') {
      item.onValueChange = (params) => accessLevelOnChange({ ...params, ...props.customProps });
    }
    resp.push(item);
    return resp;
  }, []);
  return { gridColumns };
}
